// src/components/AppsContent.tsx
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useMsal } from "@azure/msal-react";
import { Application } from "@microsoft/microsoft-graph-types";
import { 
  Box, 
  Typography, 
  CircularProgress, 
  List,
  ListItem,
  ListItemText,
  Avatar,
  Divider, 
  Paper,
  Chip,
  Button,
  IconButton,
  ListItemAvatar,
  Tooltip,
  MenuItem,
  Select,
  ToggleButtonGroup, 
  ToggleButton 
} from '@mui/material';
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CodeIcon from "@mui/icons-material/Code";
import WebIcon from "@mui/icons-material/Web";
import VerifiedIcon from '@mui/icons-material/Verified';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import BusinessIcon from '@mui/icons-material/Business';
import InfoIcon from '@mui/icons-material/Info';
import AppsIcon from '@mui/icons-material/Apps';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useAppCache } from '../context/AppCacheContext';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import ApplicationDashboard from './ApplicationDashboard';
import ViewListIcon from '@mui/icons-material/ViewList';
import DashboardIcon from '@mui/icons-material/Dashboard';

interface AppsContentProps {
  searchQuery: string;
  isMsalInitialized: boolean;
  userDisplayName: string | null;
  onAppSelect: (app: Application) => void;
}

interface PaginationConfig {
  currentPage: number;
  pageSize: number;
  totalItems: number;
}

const AppsContent: React.FC<AppsContentProps> = ({
  searchQuery,
  isMsalInitialized,
  userDisplayName,
  onAppSelect
}) => {
  // SIMPLIFIED: All data comes from context now
  const { 
    applications, 
    loading, 
    error, 
    refreshApplications,
    lastFetched,
    isStale,
    appOwners,
    currentUserId,
    loadingOwners,
    ownedAppsCount,
    spMap
  } = useAppCache();

  const { instance, accounts } = useMsal();

  function decodeToken(token: string) {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (err) {
      console.error("Failed to decode token", err);
      return null;
    }
  }


  // Local state for UI interactions only
  const [selectedAppType, setSelectedAppType] = useState<"ALL" | "OWNED" | "SPA" | "WEB" | "API" | "SINGLE_TENANT" | "MULTI_TENANT" | "WITH_SECRETS" | "SAML">("ALL");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showDashboard, setShowDashboard] = useState<boolean>(false);

  const [pagination, setPagination] = useState<PaginationConfig>({
    currentPage: 1,
    pageSize: 25,
    totalItems: 0
  });
  const [sortConfig, setSortConfig] = useState<{
    field: 'displayName' | 'appId' | 'signInAudience';
    direction: 'asc' | 'desc';
  }>({
    field: 'displayName',
    direction: 'asc'
  });

  // SIMPLIFIED: Memoized ownership lookup - cleaner implementation
  const isAppOwned = useCallback((appId: string): boolean => {
    if (!currentUserId || !appId) return false;
    const ownerInfo = appOwners[appId];
    return ownerInfo ? ownerInfo.owners.some(owner => owner.id === currentUserId) : false;
  }, [appOwners, currentUserId]);

  // SIMPLIFIED: Helper functions with better performance
  const getTenantType = useCallback((app: Application): { type: 'single' | 'multi'; label: string } => {
    const audience = app.signInAudience || '';
    
    switch (audience) {
      case 'AzureADMyOrg':
        return { type: 'single', label: 'Single tenant' };
      case 'AzureADMultipleOrgs':
      case 'AzureADandPersonalMicrosoftAccount':
        return { type: 'multi', label: 'Multi-tenant' };
      default:
        return { type: 'single', label: 'Unknown' };
    }
  }, []);

  const isSamlApp = useCallback((app: Application): boolean => {
    return app.appId ? spMap[app.appId] === "saml" : false;
  }, [spMap]);

  // SIMPLIFIED: Memoized sorting function
  const sortedApplications = useMemo(() => {
    if (searchQuery.trim()) {
      return applications; // Keep original order for search results (relevance sorted)
    }
    
    return [...applications].sort((a: Application, b: Application) => {
      let aValue: string;
      let bValue: string;
      
      switch (sortConfig.field) {
        case 'displayName':
          aValue = (a.displayName || '').toLowerCase();
          bValue = (b.displayName || '').toLowerCase();
          break;
        case 'appId':
          aValue = a.appId || '';
          bValue = b.appId || '';
          break;
        case 'signInAudience':
          aValue = a.signInAudience || '';
          bValue = b.signInAudience || '';
          break;
        default:
          aValue = (a.displayName || '').toLowerCase();
          bValue = (b.displayName || '').toLowerCase();
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [applications, sortConfig, searchQuery]);

  // Complete filteredApps function with dashboard integration
  const filteredApps = useMemo(() => {
    // Early return for empty datasets
    if (!applications.length) {
      return [];
    }

    // Use pre-sorted data when no search query, original data when searching
    const sourceApps = searchQuery.trim() ? applications : sortedApplications;
    
    // Step 1: Apply type filter first (most selective filter)
    let typeFilteredApps: Application[] = sourceApps;
    
    switch (selectedAppType) {
      case "OWNED":
        typeFilteredApps = sourceApps.filter((app: Application) => 
          app.appId && isAppOwned(app.appId)
        );
        break;
      case "SPA":
        typeFilteredApps = sourceApps.filter(
          (app: Application) => app.spa?.redirectUris && app.spa.redirectUris.length > 0
        );
        break;
      case "WEB":
        typeFilteredApps = sourceApps.filter(
          (app: Application) => app.web?.redirectUris && app.web.redirectUris.length > 0
        );
        break;
      case "API":
        typeFilteredApps = sourceApps.filter(
          (app: Application) => app.api?.oauth2PermissionScopes && app.api.oauth2PermissionScopes.length > 0
        );
        break;
      case "SINGLE_TENANT":
        typeFilteredApps = sourceApps.filter(
          (app: Application) => app.signInAudience === 'AzureADMyOrg'
        );
        break;
      case "MULTI_TENANT":
        typeFilteredApps = sourceApps.filter(
          (app: Application) => app.signInAudience && 
          ['AzureADMultipleOrgs', 'AzureADandPersonalMicrosoftAccount'].includes(app.signInAudience)
        );
        break;
      case "WITH_SECRETS":
        typeFilteredApps = sourceApps.filter(
          (app: Application) => app.passwordCredentials && app.passwordCredentials.length > 0
        );
        break;
      case "SAML":
        typeFilteredApps = sourceApps.filter((app: Application) => isSamlApp(app));
        break;
      case "ALL":
      default:
        typeFilteredApps = sourceApps;
        break;
    }
    
    // If no search query, return type-filtered results
    if (!searchQuery.trim()) {
      return typeFilteredApps;
    }
    
    // Apply search filter with shortcuts
    const query = searchQuery.toLowerCase().trim();
    
    // Search shortcuts
    const typeShortcuts: Record<string, Application[]> = {
      "owned": applications.filter((app: Application) => app.appId && isAppOwned(app.appId)),
      "my": applications.filter((app: Application) => app.appId && isAppOwned(app.appId)),
      "spa": typeFilteredApps.filter((app: Application) => app.spa?.redirectUris && app.spa.redirectUris.length > 0),
      "web": typeFilteredApps.filter((app: Application) => app.web?.redirectUris && app.web.redirectUris.length > 0),
      "api": typeFilteredApps.filter((app: Application) => app.api?.oauth2PermissionScopes && app.api.oauth2PermissionScopes.length > 0),
      "saml": typeFilteredApps.filter((app: Application) => isSamlApp(app)),
      "multi-tenant": typeFilteredApps.filter((app: Application) => 
        app.signInAudience && 
        ['AzureADMultipleOrgs', 'AzureADandPersonalMicrosoftAccount'].includes(app.signInAudience)
      ),
      "single-tenant": typeFilteredApps.filter((app: Application) => app.signInAudience === 'AzureADMyOrg'),
      "with-secrets": typeFilteredApps.filter((app: Application) => 
        app.passwordCredentials && app.passwordCredentials.length > 0
      ),
      "no-secrets": typeFilteredApps.filter((app: Application) => 
        !app.passwordCredentials || app.passwordCredentials.length === 0
      ),
      "secrets": typeFilteredApps.filter((app: Application) => 
        app.passwordCredentials && app.passwordCredentials.length > 0
      )
    };
    
    if (typeShortcuts[query]) {
      return typeShortcuts[query];
    }

    // Advanced search with performance optimization
    const escapeRegExp = (string: string): string => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const exactMatch = new RegExp(`^${escapeRegExp(query)}$`, 'i');
    const wordBoundaryMatch = new RegExp(`\\b${escapeRegExp(query)}\\b`, 'i');
    const containsMatch = new RegExp(escapeRegExp(query), 'i');
    
    interface SearchResult {
      app: Application;
      score: number;
    }
    
    const results: SearchResult[] = [];
    
    for (const app of typeFilteredApps) {
      let score = 0;
      
      // Search in display name (highest weight)
      const displayName = app.displayName || '';
      if (exactMatch.test(displayName)) {
        score += 100;
      } else if (wordBoundaryMatch.test(displayName)) {
        score += 80;
      } else if (containsMatch.test(displayName)) {
        score += 60;
      }
      
      // Search in app ID
      const appId = app.appId || '';
      if (containsMatch.test(appId)) {
        score += 40;
      }
      
      // Search in redirect URIs
      const webUris = app.web?.redirectUris || [];
      const spaUris = app.spa?.redirectUris || [];
      if ([...webUris, ...spaUris].some((uri: string) => containsMatch.test(uri))) {
        score += 30;
      }
      
      // Search in publisher domain
      if (app.publisherDomain && containsMatch.test(app.publisherDomain)) {
        score += 20;
      }
      
      if (score > 0) {
        results.push({ app, score });
      }
    }
    
    return results
      .sort((a: SearchResult, b: SearchResult) => b.score - a.score)
      .map((result: SearchResult) => result.app);
  }, [applications, sortedApplications, selectedAppType, searchQuery, isAppOwned, isSamlApp]);

  // Paginated apps
  const paginatedApps = useMemo(() => {
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    
    // Update pagination if needed
    if (pagination.totalItems !== filteredApps.length) {
      setPagination(prev => ({
        ...prev,
        totalItems: filteredApps.length,
        currentPage: Math.max(1, Math.min(prev.currentPage, Math.ceil(filteredApps.length / prev.pageSize) || 1))
      }));
    }
    
    return filteredApps.slice(startIndex, endIndex);
  }, [filteredApps, pagination.currentPage, pagination.pageSize, pagination.totalItems]);

  // Enhanced filter handler for dashboard integration
  const handleFilterChange = useCallback((newFilter: "ALL" | "OWNED" | "SPA" | "WEB" | "API" | "SINGLE_TENANT" | "MULTI_TENANT" | "WITH_SECRETS" | "SAML") => {
    setSelectedAppType(newFilter);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPagination(prev => ({
      ...prev,
      pageSize: newPageSize,
      currentPage: 1
    }));
  }, []);

  const handleAppSelect = useCallback((app: Application) => {
    onAppSelect?.(app);
  }, [onAppSelect]);

  // SIMPLIFIED: Copy to clipboard helper
  const copyToClipboard = useCallback((text: string, event: React.MouseEvent) => {
    event.stopPropagation();
    navigator.clipboard.writeText(text);
  }, []);

  // UI Helper functions
  const getFilterButtonStyle = useCallback((isSelected: boolean) => ({
    borderColor: isSelected ? '#2c3e50' : 'rgba(44, 62, 80, 0.3)',
    color: isSelected ? '#ffffff' : '#2c3e50',
    backgroundColor: isSelected ? '#2c3e50' : 'transparent',
    '&:hover': {
      backgroundColor: isSelected ? '#34495e' : 'rgba(44, 62, 80, 0.08)',
      borderColor: '#2c3e50'
    }
  }), []);

  const generatePageNumbers = useCallback((current: number, total: number): (number | string)[] => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    
    const pages: (number | string)[] = [1];
    
    if (current > 3) pages.push('...');
    
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    
    for (let i = start; i <= end; i++) {
      if (!pages.includes(i)) pages.push(i);
    }
    
    if (current < total - 2) pages.push('...');
    if (total > 1) pages.push(total);
    
    return pages;
  }, []);

  // IMPROVED: Refresh indicator with better UX
  const RefreshIndicator: React.FC = () => {
    if (!isStale || loading) return null;
    
    const minutesOld = lastFetched ? Math.round((Date.now() - lastFetched.getTime()) / 60000) : 0;
    
    return (
      <Box sx={{
        mb: 2,
        p: 1.5,
        bgcolor: 'rgba(255, 152, 0, 0.08)',
        border: '1px solid rgba(255, 152, 0, 0.2)',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="body2" color="text.secondary">
          Application data is {minutesOld > 0 ? `${minutesOld} minutes old` : 'outdated'}
          {loadingOwners && <span> • Loading ownership data...</span>}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={refreshApplications}
          startIcon={<RefreshIcon />}
          sx={{ ml: 2 }}
        >
          Refresh
        </Button>
      </Box>
    );
  };

  // Pagination controls component
  const PaginationControls: React.FC = () => {
    const totalPages = Math.ceil(pagination.totalItems / pagination.pageSize);
    
    if (pagination.totalItems <= pagination.pageSize) {
      return null;
    }
    
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mt: 2, 
        p: 2,
        bgcolor: 'rgba(44, 62, 80, 0.02)',
        borderRadius: 1,
        border: '1px solid rgba(44, 62, 80, 0.08)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Items per page:
          </Typography>
          <Select
            size="small"
            value={pagination.pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            sx={{ 
              minWidth: 80,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(44, 62, 80, 0.2)'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#2c3e50'
              }
            }}
          >
            {[10, 25, 50, 100].map(size => (
              <MenuItem key={size} value={size}>{size}</MenuItem>
            ))}
          </Select>
        </Box>
        
        <Typography variant="body2" color="text.secondary">
          Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1}-
          {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)} of {pagination.totalItems}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            disabled={pagination.currentPage === 1}
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            sx={{
              color: '#2c3e50',
              '&:disabled': { color: 'rgba(44, 62, 80, 0.3)' }
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {generatePageNumbers(pagination.currentPage, totalPages).map((page, index) => (
              <Button
                key={index}
                size="small"
                variant={page === pagination.currentPage ? "contained" : "text"}
                onClick={() => typeof page === 'number' ? handlePageChange(page) : undefined}
                disabled={typeof page !== 'number'}
                sx={{ 
                  minWidth: 32, 
                  height: 32,
                  bgcolor: page === pagination.currentPage ? '#2c3e50' : 'transparent',
                  color: page === pagination.currentPage ? '#ffffff' : '#2c3e50',
                  '&:hover': {
                    bgcolor: page === pagination.currentPage ? '#34495e' : 'rgba(44, 62, 80, 0.08)'
                  },
                  '&:disabled': {
                    color: 'rgba(44, 62, 80, 0.3)'
                  }
                }}
              >
                {page}
              </Button>
            ))}
          </Box>
          
          <IconButton
            size="small"
            disabled={pagination.currentPage >= totalPages}
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            sx={{
              color: '#2c3e50',
              '&:disabled': { color: 'rgba(44, 62, 80, 0.3)' }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>
    );
  };

  // SIMPLIFIED: Loading and error states
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          {applications.length > 0 ? 'Refreshing applications...' : 'Loading applications...'}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        {error}
      </Box>
    );
  }

return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <RefreshIndicator />

      {/* Dashboard toggle button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
        <Button
          size="small"
          startIcon={<DashboardIcon />}
          onClick={() => setShowDashboard(v => !v)}
          variant="outlined"
        >
          {showDashboard ? 'Hide Dashboard' : 'Show Dashboard'}
        </Button>
      </Box>

      {/* Conditional Dashboard */}
      {showDashboard && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            bgcolor: 'rgba(25, 118, 210, 0.04)',
            border: '1px solid rgba(25, 118, 210, 0.15)'
          }}
        >
          <ApplicationDashboard onFilterSelect={handleFilterChange} />
        </Paper>
      )}

      {/* Divider only when dashboard visible */}
      {showDashboard && <Divider sx={{ my: 2 }} />}

      {/* Simplified Filters Section - VISUAL ENHANCEMENT: cleaner toolbar layout */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          mb: 2, 
          mt: showDashboard ? 4 : 2,
          borderRadius: 2,
          bgcolor: 'rgba(250, 250, 250, 0.6)', 
          border: '1px solid rgba(44, 62, 80, 0.08)'
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          {/* Filter buttons and toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              value={selectedAppType}
              exclusive
              onChange={(e, newValue) => {
                if (newValue !== null) {
                  handleFilterChange(newValue);
                }
              }}
              aria-label="application type filter"
              size="small"
            >
              <ToggleButton 
                value="ALL" 
                aria-label="all applications"
              >
                <AppsIcon sx={{ mr: 0.5 }} fontSize="small" />
                All Apps ({applications.length})
              </ToggleButton>
              
              <ToggleButton 
                value="OWNED" 
                aria-label="my applications"
                disabled={!currentUserId}
              >
                <PersonIcon sx={{ mr: 0.5 }} fontSize="small" />
                My Apps {loadingOwners ? '(...)' : `(${ownedAppsCount})`}
              </ToggleButton>
              
              <ToggleButton 
                value="SPA" 
                aria-label="SPA applications"
              >
                <CodeIcon sx={{ mr: 0.5 }} fontSize="small" />
                SPA
              </ToggleButton>
              
              <ToggleButton 
                value="WEB" 
                aria-label="Web applications"
              >
                <WebIcon sx={{ mr: 0.5 }} fontSize="small" />
                Web
              </ToggleButton>
              
              <ToggleButton 
                value="API" 
                aria-label="API applications"
              >
                <SettingsEthernetIcon sx={{ mr: 0.5 }} fontSize="small" />
                API
              </ToggleButton>
            </ToggleButtonGroup>

            {!showDashboard && selectedAppType !== 'ALL' && (
              <Chip
                label={`Filtered: ${selectedAppType.replace('_', ' ')}`}
                size="small"
                onDelete={() => handleFilterChange('ALL')}
              />
            )}
          </Box>

          {/* Refresh button */}
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={refreshApplications}
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>
      </Paper>    
    

      {/* Search relevance indicator */}
      {searchQuery.trim() && (
        <Box sx={{ 
          mb: 2, 
          p: 1, 
          bgcolor: 'rgba(33, 150, 243, 0.08)', 
          borderRadius: 1,
          border: '1px solid rgba(33, 150, 243, 0.2)'
        }}>
          <Typography variant="caption" color="text.secondary">
            Search results sorted by relevance • Clear search to enable alphabetical sorting
          </Typography>
        </Box>
      )}

      {/* Application List */}
      {filteredApps.length === 0 ? (
        <Box sx={{ 
          py: 3, 
          textAlign: 'center',
          backgroundColor: 'rgba(0,0,0,0.02)', 
          borderRadius: 2,
          border: '1px dashed rgba(0,0,0,0.2)'
        }}>
          <Typography variant="body1" color="text.secondary">
            {selectedAppType === "ALL" 
              ? "No applications found." 
              : selectedAppType === "OWNED"
                ? "No applications you own found."
                : `No ${selectedAppType} applications found.`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {searchQuery 
              ? 'Try a different search term.' 
              : selectedAppType === "ALL"
                ? 'Register a new application to get started.'
                : selectedAppType === "OWNED"
                  ? loadingOwners ? 'Loading ownership data...' : 'You are not listed as an owner of any applications.'
                  : `Register a new ${selectedAppType} application to get started.`}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          {/* Summary count with inline sort toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {filteredApps.length > pagination.pageSize ? (
                  <>
                    Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1}-
                    {Math.min(pagination.currentPage * pagination.pageSize, filteredApps.length)} of {filteredApps.length} applications
                    {searchQuery && <span> for search "{searchQuery}"</span>}
                  </>
                ) : (
                  <>
                    {filteredApps.length} {filteredApps.length === 1 ? "application" : "applications"} found
                    {searchQuery && <span> for search "{searchQuery}" across name, ID, and URIs</span>}
                  </>
                )}
              </Typography>
              <Tooltip title="Search examples: 'single-tenant', 'multi-tenant', app name, app ID, or redirect URI" placement="right">
                <InfoIcon sx={{ ml: 0.5, fontSize: '0.875rem', color: 'text.secondary', opacity: 0.7, cursor: 'help' }} />
              </Tooltip>
            </Box>

            {/* Compact sort toggle - only show when not searching */}
            {!searchQuery.trim() && (
              <Tooltip title={`Sort by name ${sortConfig.direction === 'asc' ? 'descending' : 'ascending'}`} placement="left">
                <IconButton
                  size="small"
                  onClick={() => setSortConfig(prev => ({
                    field: 'displayName',
                    direction: prev.direction === 'asc' ? 'desc' : 'asc'
                  }))}
                  sx={{
                    color: '#2c3e50',
                    '&:hover': {
                      bgcolor: 'rgba(44, 62, 80, 0.08)'
                    }
                  }}
                >
                  {sortConfig.direction === 'asc' ? (
                    <ArrowUpwardIcon fontSize="small" />
                  ) : (
                    <ArrowDownwardIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
          
          {/* Applications list */}
          <List sx={{ 
            width: '100%', 
            bgcolor: 'background.paper',
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.08)'
          }}>
            {paginatedApps.map((app, index) => (
              <React.Fragment key={app.id || index}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  sx={{ 
                    py: 1,
                    px: 2,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(44, 62, 80, 0.04)'
                    }
                  }}
                  secondaryAction={
                    <Button 
                      variant="outlined"
                      size="small"
                      onClick={() => handleAppSelect(app)}
                      sx={{ 
                        borderRadius: 2,
                        borderColor: '#2c3e50',
                        color: '#2c3e50',
                        ml: 2,
                        '&:hover': {
                          borderColor: '#34495e',
                          backgroundColor: 'rgba(44, 62, 80, 0.08)'
                        }
                      }}
                    >
                      Select
                    </Button>
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{ 
                        bgcolor: 'rgba(44, 62, 80, 0.12)',
                        color: '#2c3e50'
                      }}
                    >
                      {/* SIMPLIFIED: App type detection */}
                      {app.api?.oauth2PermissionScopes?.length && !app.spa?.redirectUris?.length && !app.web?.redirectUris?.length
                        ? <SettingsEthernetIcon fontSize="small" />
                        : app.spa?.redirectUris?.length 
                          ? <CodeIcon fontSize="small" />
                          : app.web?.redirectUris?.length
                            ? <WebIcon fontSize="small" />
                            : <AppsIcon fontSize="small" />
                      }
                    </Avatar>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 600,
                          fontSize: '1.05rem',
                          pr: 2,
                          color: '#2c3e50', 
                          letterSpacing: '0.01em',
                          display: 'flex',
                          alignItems: 'center',
                          '&:hover': {
                            textDecoration: 'underline',
                            textDecorationColor: 'rgba(44, 62, 80, 0.4)',
                            textDecorationThickness: '2px',
                            textUnderlineOffset: '3px'
                          }
                        }} 
                        noWrap
                      >
                        {app.displayName}

                        {/* SIMPLIFIED: Show ownership indicator */}
                        {app.appId && isAppOwned(app.appId) && (
                          <Tooltip title="You are an owner of this application">
                            <PersonIcon sx={{ 
                              fontSize: '1rem', 
                              ml: 0.5, 
                              color: '#2c3e50',
                              opacity: 0.7
                            }} />
                          </Tooltip>
                        )}
                        
                        {app.tags?.includes('important') && (
                          <Tooltip title="Important Application">
                            <VerifiedIcon sx={{ 
                              fontSize: '1rem', 
                              ml: 0.5, 
                              color: '#2c3e50'
                            }} />
                          </Tooltip>
                        )}
                      </Typography>
                    }
                    
                    secondary={
                      <Typography component="div" variant="body2" color="text.secondary">
                        {/* Single row - App ID, Tenant Type, and all Chips including Redirect URI */}
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.25 }}>
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ 
                              display: 'inline-flex',
                              alignItems: 'center'
                            }}
                          >
                            <FingerprintIcon sx={{ fontSize: '0.875rem', mr: 0.5, opacity: 0.7 }} />
                            {app.appId ? (
                              <>
                                {`${app.appId.substring(0, 8)}...${app.appId.substring(app.appId.length - 4)}`}
                                <Tooltip title="Copy App ID">
                                  <IconButton 
                                    size="small" 
                                    sx={{ ml: 0.5 }}
                                    onClick={(e) => copyToClipboard(app.appId || "", e)}
                                  >
                                    <ContentCopyIcon sx={{ fontSize: '0.75rem' }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : 'N/A'}
                          </Typography>
                          
                          {/* Tenant type chip */}
                          {app.signInAudience && (
                            <Chip
                              icon={getTenantType(app).type === 'multi' ? (
                                <PublicIcon sx={{ fontSize: '0.75rem !important' }} />
                              ) : (
                                <BusinessIcon sx={{ fontSize: '0.75rem !important' }} />
                              )}
                              label={getTenantType(app).label}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                height: 20, 
                                fontSize: '0.6875rem',
                                borderColor: getTenantType(app).type === 'multi' 
                                  ? 'rgba(33, 150, 243, 0.3)' 
                                  : 'rgba(0,0,0,0.12)',
                                backgroundColor: getTenantType(app).type === 'multi' 
                                  ? 'rgba(33, 150, 243, 0.08)' 
                                  : 'transparent',
                                color: getTenantType(app).type === 'multi' 
                                  ? '#1976d2' 
                                  : 'text.secondary'
                              }}
                            />
                          )}

                          {/* Roles chip */}
                          {app.appRoles && app.appRoles.length > 0 && (
                            <Chip
                              label={`${app.appRoles.length} Role${app.appRoles.length !== 1 ? 's' : ''}`}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                height: 20, 
                                fontSize: '0.6875rem',
                                borderColor: 'rgba(0,0,0,0.12)'
                              }}
                            />
                          )}
                          
                          {/* Scopes chip */}
                          {app.api?.oauth2PermissionScopes && app.api.oauth2PermissionScopes.length > 0 && (
                            <Chip
                              label={`${app.api.oauth2PermissionScopes.length} Scope${app.api.oauth2PermissionScopes.length !== 1 ? 's' : ''}`}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                height: 20, 
                                fontSize: '0.6875rem',
                                borderColor: 'rgba(0,0,0,0.12)'
                              }}
                            />
                          )}
                          
                          {/* Redirect URI indicator - icon only */}
                          {(app.web?.redirectUris?.[0] || app.spa?.redirectUris?.[0]) && (
                            <Tooltip title={app.web?.redirectUris?.[0] || app.spa?.redirectUris?.[0] || ''}>
                              <Chip
                                icon={<LinkIcon sx={{ fontSize: '0.75rem !important' }} />}
                                label=""
                                size="small"
                                sx={{ 
                                  height: 20,
                                  width: 28,
                                  minWidth: 28,
                                  '& .MuiChip-label': { 
                                    display: 'none' 
                                  },
                                  '& .MuiChip-icon': { 
                                    margin: 0 
                                  },
                                  backgroundColor: 'rgba(76, 175, 80, 0.12)',
                                  color: '#4caf50'
                                }}
                              />
                            </Tooltip>
                          )}
                          
                          {/* Client secrets indicator - icon only */}
                          {app.passwordCredentials && app.passwordCredentials.length > 0 && (
                            <Tooltip title="Has client secret">
                              <Chip
                                icon={<LockIcon sx={{ fontSize: '0.75rem !important' }} />}
                                label=""
                                size="small"
                                sx={{ 
                                  height: 20,
                                  width: 28,
                                  minWidth: 28,
                                  '& .MuiChip-label': { 
                                    display: 'none' 
                                  },
                                  '& .MuiChip-icon': { 
                                    margin: 0 
                                  },
                                  backgroundColor: 'rgba(44, 62, 80, 0.12)',
                                  color: '#2c3e50'
                                }}
                              />
                            </Tooltip>
                          )}
                          
                          {/* SAML indicator - icon only */}
                          {isSamlApp(app) && (
                            <Tooltip title="SAML application">
                              <Chip
                                icon={<SecurityIcon sx={{ fontSize: '0.75rem !important' }} />}
                                label=""
                                size="small"
                                sx={{ 
                                  height: 20,
                                  width: 28,
                                  minWidth: 28,
                                  '& .MuiChip-label': { 
                                    display: 'none' 
                                  },
                                  '& .MuiChip-icon': { 
                                    margin: 0 
                                  },
                                  backgroundColor: 'rgba(156, 39, 176, 0.12)',
                                  color: '#9c27b0'
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </Typography>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
          <PaginationControls />
        </Box>
      )}
    </Box>
  );
};

export default AppsContent;